import { useRef, useState } from "react";
import { Camera, Loader2, X } from "lucide-react";
import api, { formatError } from "@/lib/api";
import ImageCropDialog from "@/components/home/ImageCropDialog";

/**
 * CompanyAvatarUploader — Upload/Preview für das Unternehmens-Logo (Platform-Avatar).
 * Wird als Autor-Avatar für Platform-Posts genutzt.
 * Nutzt denselben /api/upload-Endpoint wie User-Avatare (area_scope=PROFILE_AVATAR).
 * iter155 · Mit Crop-Dialog (1:1 rund) analog zum User-Avatar-Upload.
 */
export default function CompanyAvatarUploader({ value, onChange, title, aspect = 1, darkBg = false, pngOutput = false }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cropSource, setCropSource] = useState(null);
  const isRound = aspect === 1;

  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setError("");
    setCropSource(f);
  };

  const uploadCropped = async (blob) => {
    setCropSource(null);
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", blob);
      const { data } = await api.post("/upload?area_scope=PROFILE_AVATAR", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = `/api/files/${data.path}`;
      onChange(url);
    } catch (err) {
      setError(formatError(err) || "Upload fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const clear = () => {
    onChange("");
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="border border-[#E5E5E5] rounded-lg p-4">
      <label className="text-[10px] font-bold text-[#999] uppercase tracking-wider block mb-2">
        {title || "Unternehmens-Avatar (Logo für Platform-Posts)"}
      </label>
      <div className="flex items-center gap-4">
        <div className="relative">
          {value ? (
            <img
              src={value}
              alt="Logo"
              data-testid="company-avatar-preview"
              className={
                isRound
                  ? "w-20 h-20 rounded-full object-cover border-2 border-[#E5E5E5]"
                  : `h-16 w-48 rounded-lg object-contain border-2 border-[#E5E5E5] p-2 ${darkBg ? "bg-[#0A0A0A]" : "bg-white"}`
              }
            />
          ) : (
            <div
              data-testid="company-avatar-placeholder"
              className={
                isRound
                  ? "w-20 h-20 rounded-full bg-[#F5F5F5] border-2 border-dashed border-[#E5E5E5] flex items-center justify-center text-[#999]"
                  : `h-16 w-48 rounded-lg border-2 border-dashed border-[#E5E5E5] flex items-center justify-center ${darkBg ? "bg-[#0A0A0A] text-white/50" : "bg-[#F5F5F5] text-[#999]"}`
              }
            >
              <Camera size={20} />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            data-testid="company-avatar-upload-btn"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#0A0A0A] text-white text-xs font-medium hover:bg-[#222] disabled:opacity-50"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />}
            {value ? "Ersetzen" : "Hochladen"}
          </button>
          {value && (
            <button
              type="button"
              onClick={clear}
              disabled={busy}
              data-testid="company-avatar-remove-btn"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-[#E5E5E5] text-red-500 text-xs hover:bg-red-50"
            >
              <X size={12} /> Entfernen
            </button>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={onFile}
          className="hidden"
          data-testid="company-avatar-file-input"
        />
      </div>
      {error && <p className="mt-2 text-xs text-red-500">{error}</p>}
      {isRound && (
        <p className="mt-2 text-[10px] text-[#999]">
          Nach dem Speichern wird dieses Logo als Autor-Avatar bei allen offiziellen Platform-Beiträgen verwendet.
        </p>
      )}
      {cropSource && (
        <ImageCropDialog
          file={cropSource}
          aspect={aspect}
          outputType={pngOutput ? "image/png" : "image/jpeg"}
          title={isRound ? "Logo-Ausschnitt wählen (rund)" : "Logo-Ausschnitt wählen (3:1)"}
          onCancel={() => setCropSource(null)}
          onCropped={uploadCropped}
        />
      )}
    </div>
  );
}
